// src/utils/royalty.js
//
// The maths behind the /quote page's "Retail pricing & royalties" panel: what each sale
// pays each party, whether the print run pays for itself, and where the flow diagram's
// bands sit. Pure — no React, no DOM — so this is the part with tests.
//
// None of this is print pricing. The one print figure it uses, what a copy costs after
// the volume discount, arrives from POST /api/quotes/calculate as `net_price_per_unit`;
// nothing here derives it. The rest is transcribed from the owner's prototype
// (book-pricing-estimator-5), including its choice to take every share out of what is
// left after the print cost.
//
// One departure, decided on the ticket: the prototype let the author edit ReVisionari's
// share. Here it is locked to the publishing tiers /pricing advertises (Pricing.jsx), 15%
// or 35% over 24 months, because that is what the platform actually takes.
//
// routes/drafts.py keeps the same lists (SALES_PLAN_*) to validate a saved plan; a value
// this file can produce that isn't listed there is dropped on save. Change both together.

export const PLATFORM_TIERS = [
  { id: 'no-frills', name: 'No-Frills', pct: 15 },
  { id: 'full-service', name: 'Full-Service', pct: 35 },
];
/** Both tiers carry the same exclusive term. */
export const PLATFORM_TERM = '24m';

export const ROY_TERMS = [
  ['1y', '1 year'],
  ['2y', '2 years'],
  ['3y', '3 years'],
  ['5y', '5 years'],
  ['10y', '10 years'],
  ['copyright', 'Life of copyright'],
  ['terminated', 'Until terminated'],
];
export const ROY_ROLES = ['Co-author', 'Illustrator', 'Editor', 'Translator', 'Agent', 'Other'];

/**
 * Collaborators take theme.js `chart` colours in this order. Six, so no two parties
 * ever share a colour; the prototype cycled its list and repeated one at the seventh.
 */
export const COLLABORATOR_SERIES = ['teal', 'terracotta', 'orchid', 'sage', 'sky', 'rose'];
export const MAX_COLLABORATORS = COLLABORATOR_SERIES.length;

export const FORMAT_NAMES = { paperback: 'Paperback', ebook: 'eBook', bundle: 'Bundle' };

/** The panel's starting values — the prototype's. Prices and counts are input text. */
export function defaultPlan() {
  return {
    prices: { paperback: '35', ebook: '19', bundle: '45' },
    bundleOn: false,
    sold: { paperback: '20', ebook: '0', bundle: '0' },
    platformTier: 'no-frills',
    parties: [
      { id: 'platform', name: 'ReVisionari', pct: 15, term: PLATFORM_TERM, series: 'platform' },
      { id: 'author', name: 'You', role: 'Author', pct: 85, term: 'perpetual', series: 'author' },
    ],
  };
}

// ---- small helpers --------------------------------------------------------------------

/** A typed number, or null for blank / not a number. */
export const num = (value) => {
  const n = parseFloat(value);
  return Number.isNaN(n) ? null : n;
};

export const round2 = (n) => Math.round(n * 100) / 100;

const whole = (value) => Math.max(0, Math.floor(num(value) || 0));

export const money = (n) =>
  `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Signed money: "-$3.20" rather than "$-3.20". */
export const signedMoney = (n) => `${n < 0 ? '-' : ''}${money(Math.abs(n))}`;

// ---- formats and split ----------------------------------------------------------------

/** Which formats are on sale. A blank or zero eBook price means no eBook. */
export function formatsOn(plan) {
  const ebook = num(plan.prices.ebook);
  return { paperback: true, ebook: ebook !== null && ebook > 0, bundle: !!plan.bundleOn };
}

export const totalPct = (parties) => parties.reduce((sum, p) => sum + (Number(p.pct) || 0), 0);

/**
 * The line above the split table. Over 100% is an error; under is just unfinished,
 * so it says what's left rather than turning red.
 */
export function splitStatus(parties) {
  const total = round2(totalPct(parties));
  if (total === 100) return { total, state: 'ok', label: '100% allocated' };
  if (total < 100) {
    return { total, state: 'under', label: `${total}% allocated · ${round2(100 - total)}% not assigned yet` };
  }
  return { total, state: 'over', label: `${total}% allocated · ${round2(total - 100)}% over` };
}

export function partyLabel(party) {
  if (party.id === 'platform') return 'Platform fee';
  if (party.id === 'author') return 'Author share';
  const name = (party.name || '').trim();
  return name || `${party.role || 'Collaborator'} share`;
}

export function termText(term) {
  if (term === 'perpetual') return 'Perpetual';
  if (term === PLATFORM_TERM) return '24 months';
  return (ROY_TERMS.find(([value]) => value === term) || [null, ''])[1];
}

// ---- editing the plan -----------------------------------------------------------------

/**
 * Switch ReVisionari's tier. The author's share absorbs the difference, so a split that
 * added up to 100% still does (floored at 0).
 */
export function withPlatformTier(plan, tierId) {
  const tier = PLATFORM_TIERS.find((t) => t.id === tierId);
  const platform = plan.parties.find((p) => p.id === 'platform');
  if (!tier || !platform) return plan;
  const delta = tier.pct - platform.pct;
  return {
    ...plan,
    platformTier: tier.id,
    parties: plan.parties.map((p) => {
      if (p.id === 'platform') return { ...p, pct: tier.pct };
      if (p.id === 'author') return { ...p, pct: Math.max(0, round2(p.pct - delta)) };
      return p;
    }),
  };
}

export const collaboratorCount = (plan) => plan.parties.filter((p) => p.series !== 'platform' && p.series !== 'author').length;

/**
 * Add a blank collaborator with the first colour nobody is using — so removing one and
 * adding another never gives two people the same colour.
 */
export function addCollaborator(plan) {
  if (collaboratorCount(plan) >= MAX_COLLABORATORS) return plan;
  const used = new Set(plan.parties.map((p) => p.series));
  const series = COLLABORATOR_SERIES.find((s) => !used.has(s));
  const ids = plan.parties.map((p) => (/^c(\d+)$/.exec(p.id) || [])[1]).filter(Boolean).map(Number);
  const id = `c${Math.max(0, ...ids) + 1}`;
  return {
    ...plan,
    parties: [...plan.parties, { id, name: '', role: 'Co-author', pct: 0, term: '5y', series }],
  };
}

export function updateParty(plan, id, changes) {
  return { ...plan, parties: plan.parties.map((p) => (p.id === id ? { ...p, ...changes } : p)) };
}

/** Platform and author rows can't be removed. */
export function removeParty(plan, id) {
  if (id === 'platform' || id === 'author') return plan;
  return { ...plan, parties: plan.parties.filter((p) => p.id !== id) };
}

// ---- one sale -------------------------------------------------------------------------

/**
 * Where one sale of `format` goes. Shares come out of what's left after the print cost
 * (an eBook has none). An over-allocated split is scaled down to 100%; an under-allocated
 * one leaves an unallocated remainder.
 */
export function saleBreakdown(plan, format, unitCost) {
  const retail = Math.max(0, num(plan.prices[format]) || 0);
  const print = format === 'ebook' ? 0 : unitCost;
  const net = retail - print;
  const allocated = totalPct(plan.parties);
  const base = Math.max(100, allocated);
  const shares = plan.parties.map((party) => ({
    party,
    value: net > 0 ? (net * (party.pct || 0)) / base : 0,
  }));
  const unallocated = net > 0 && allocated < 100 ? (net * (100 - allocated)) / 100 : 0;
  const author = shares.find((s) => s.party.id === 'author');
  return { format, retail, print, net, shares, unallocated, totalPct: allocated, authorNet: author ? author.value : 0 };
}

/** The author's money from one sale. Below the print cost it is the loss on the sale. */
export const yourNet = (breakdown) => (breakdown.net > 0 ? breakdown.authorNet : breakdown.net);

// ---- the whole run --------------------------------------------------------------------

/**
 * Everything the panel shows: each format's breakdown, the author's net per sale, and
 * the profitability gauge.
 *
 * Profit = the author's shares on every estimated sale, plus the print cost recovered on
 * printed copies sold (the author paid for the run up front), minus what the run cost.
 * `quote` supplies the run: `unitCost` (net_price_per_unit), `qty` and `total`.
 */
export function projectPlan(plan, { unitCost = 0, qty = 0, total = 0 } = {}) {
  const on = formatsOn(plan);
  const breakdowns = {
    paperback: saleBreakdown(plan, 'paperback', unitCost),
    ebook: saleBreakdown(plan, 'ebook', unitCost),
    bundle: saleBreakdown(plan, 'bundle', unitCost),
  };
  const { paperback: pb, ebook: eb, bundle: bu } = breakdowns;
  const perSale = { paperback: yourNet(pb), ebook: eb.authorNet, bundle: yourNet(bu) };

  const sold = {
    paperback: whole(plan.sold.paperback),
    ebook: on.ebook ? whole(plan.sold.ebook) : 0,
    bundle: on.bundle ? whole(plan.sold.bundle) : 0,
  };
  const printedSold = Math.min(qty, sold.paperback + sold.bundle);
  const earnings = sold.paperback * perSale.paperback + sold.ebook * perSale.ebook + sold.bundle * perSale.bundle;
  const recovered = printedSold * unitCost;
  const profit = earnings + recovered - total;
  const ratio = total > 0 ? Math.max(-1, Math.min(1, profit / total)) : 0;

  // How many paperbacks would cover the run, given the eBooks and bundles already estimated.
  // null: nothing to say (in profit, or a paperback earns nothing). Infinity: more than
  // were printed.
  let breakEven = null;
  const perPaperback = pb.net > 0 ? pb.authorNet + unitCost : 0;
  if (profit < 0 && perPaperback > 0) {
    const bundleCash = (bu.net > 0 ? bu.authorNet : 0) + unitCost;
    const need = Math.ceil((total - sold.ebook * eb.authorNet - sold.bundle * bundleCash) / perPaperback);
    breakEven = need <= qty ? need : Infinity;
  }

  return {
    on,
    breakdowns,
    perSale,
    sold,
    earnings,
    recovered,
    salesTotal: earnings + recovered,
    profit,
    ratio,
    breakEven,
    overPrinted: sold.paperback + sold.bundle > qty,
  };
}

/** The hint under the bundle price. */
export function bundleNote(plan) {
  const on = formatsOn(plan);
  if (!on.bundle) return 'Print + eBook';
  const separately = (num(plan.prices.paperback) || 0) + (on.ebook ? num(plan.prices.ebook) : 0);
  const bundle = num(plan.prices.bundle) || 0;
  return bundle < separately ? `Saves readers ${money(separately - bundle)}` : `Usually priced under ${money(separately)}`;
}

// ---- the flow diagram -----------------------------------------------------------------

/**
 * Positions for the flow diagram of one sale: the retail price on the left, one band per
 * slice on the right, joined by ribbons. A single fan-out, so no general Sankey layout is
 * needed. Band heights follow each slice's value but never drop below a minimum, so a 2%
 * share still has room for its label.
 *
 * `width` is the space available; below 520 the labels stack. `colors` is theme.js
 * `chart`. The prototype's numbers, unchanged.
 */
export function flowLayout(breakdown, { width, colors }) {
  const b = breakdown;
  const W = Math.max(300, width || 0);
  const narrow = W < 520;

  const nodes = [];
  if (b.print > 0) {
    nodes.push({ key: 'print', label: 'Print cost', value: Math.min(b.print, b.retail), shown: b.print, color: colors.print, sub: '' });
  }
  b.shares.forEach(({ party, value }) => {
    if (party.pct > 0 || party.id === 'author') {
      nodes.push({
        key: party.id,
        label: `${partyLabel(party)} (${party.pct}%)`,
        value,
        shown: value,
        color: colors[party.series],
        sub: termText(party.term),
      });
    }
  });
  if (b.unallocated > 0) {
    nodes.push({
      key: 'unassigned',
      label: `Unallocated (${round2(100 - b.totalPct)}%)`,
      value: b.unallocated,
      shown: b.unallocated,
      color: colors.unassigned,
      sub: '',
    });
  }

  const fs = narrow ? 11.5 : 13;
  const minH = narrow ? 40 : 30;
  const gap = narrow ? 8 : 10;
  const avail = Math.max(200, nodes.length * (minH + 8)) - gap * (nodes.length - 1);
  const sum = nodes.reduce((s, n) => s + n.value, 0) || 1;
  nodes.forEach((n) => { n.h = Math.max(minH, (n.value / sum) * avail); });
  const srcH = nodes.reduce((s, n) => s + n.h, 0);
  const H = srcH + gap * (nodes.length - 1) + 4;

  const srcW = Math.round(narrow ? Math.max(78, W * 0.26) : Math.min(170, W * 0.2));
  const edgeW = narrow ? 6 : 8;
  const nodeX = srcW + edgeW + Math.round(narrow ? Math.max(36, W * 0.13) : Math.min(230, W * 0.24));
  const srcY = (H - srcH) / 2;
  const sx = srcW + edgeW;
  const mx = (sx + nodeX) / 2;

  let sy = srcY;
  let ny = 2;
  nodes.forEach((n) => {
    const h = n.h;
    n.y = ny;
    n.cy = ny + h / 2;
    n.ribbon = `M${sx},${sy} C${mx},${sy} ${mx},${ny} ${nodeX},${ny} L${nodeX},${ny + h} C${mx},${ny + h} ${mx},${sy + h} ${sx},${sy + h} Z`;
    n.twoLine = !narrow && !!n.sub && h >= 44;
    sy += h;
    ny += h + gap;
  });

  return { W, H, narrow, fs, srcW, srcH, srcY, edgeW, nodeX, bandEnd: W, nodes };
}

/** The notes under the diagram: a price below the print cost, and an over-allocated split. */
export function flowWarnings(breakdown) {
  const b = breakdown;
  const name = FORMAT_NAMES[b.format];
  const msgs = [];
  if (b.net <= 0 && b.print > 0) {
    msgs.push(`${name} price is ${money(b.print - b.retail)} below the ${money(b.print)} print cost — every sale loses money and no royalties are paid.`);
  }
  if (b.totalPct > 100) {
    msgs.push(`Shares add up to ${round2(b.totalPct)}% — shown scaled to 100%. Reduce them by ${round2(b.totalPct - 100)}%.`);
  }
  return msgs;
}

// ---- saving ---------------------------------------------------------------------------

/**
 * The plan as POST /api/drafts and PUT /api/drafts/<id> take it (`sales_plan`). Inputs
 * only — every figure above is recomputed from these. Formats that are off are saved as
 * null / 0, not as their leftover input text.
 */
export function toSalesPlan(plan) {
  const on = formatsOn(plan);
  return {
    platform_tier: plan.platformTier,
    prices: {
      paperback: num(plan.prices.paperback),
      ebook: on.ebook ? num(plan.prices.ebook) : null,
      bundle: on.bundle ? num(plan.prices.bundle) : null,
    },
    estimates: {
      paperback: whole(plan.sold.paperback),
      ebook: on.ebook ? whole(plan.sold.ebook) : 0,
      bundle: on.bundle ? whole(plan.sold.bundle) : 0,
    },
    split: plan.parties.map((p) => {
      if (p.id === 'platform') return { party: 'platform', pct: p.pct, term: PLATFORM_TERM };
      if (p.id === 'author') return { party: 'author', pct: p.pct, term: 'perpetual' };
      return { party: 'collaborator', name: (p.name || '').trim(), role: p.role, pct: p.pct, term: p.term };
    }),
  };
}

/**
 * A saved `sales_plan` back as the panel's plan: the inverse of toSalesPlan, for the in-app
 * estimator that opens on a manuscript. What arrives was written by
 * routes/drafts.py::_sanitize_sales_plan, so a value it couldn't keep comes back null and
 * is filled here. No plan at all (a manuscript from before the royalty panel, or one never
 * priced) is defaultPlan.
 *
 * A format saved as off comes back off: a null eBook price is a blank eBook price, and a
 * null bundle price unticks the bundle (its price box keeps the default for next time).
 * ReVisionari's share follows the tier, as it does on the page; without a tier, the saved
 * percentage picks one. Without an author row, the author takes what the others leave.
 */
export function fromSalesPlan(saved) {
  const base = defaultPlan();
  if (!saved || typeof saved !== 'object') return base;

  const prices = saved.prices || {};
  const estimates = saved.estimates || {};
  const split = Array.isArray(saved.split) ? saved.split.filter(Boolean) : [];
  const text = (value, fallback) => (typeof value === 'number' && Number.isFinite(value) ? String(value) : fallback);

  const platformRow = split.find((r) => r.party === 'platform');
  const authorRow = split.find((r) => r.party === 'author');
  const tier =
    PLATFORM_TIERS.find((t) => t.id === saved.platform_tier) ||
    PLATFORM_TIERS.find((t) => platformRow && t.pct === platformRow.pct) ||
    PLATFORM_TIERS[0];

  const collaborators = split
    .filter((r) => r.party === 'collaborator')
    .slice(0, MAX_COLLABORATORS)
    .map((r, i) => ({
      id: `c${i + 1}`,
      name: typeof r.name === 'string' ? r.name : '',
      role: ROY_ROLES.includes(r.role) ? r.role : 'Other',
      pct: Number(r.pct) || 0,
      term: ROY_TERMS.some(([value]) => value === r.term) ? r.term : '5y',
      series: COLLABORATOR_SERIES[i],
    }));

  const authorPct = authorRow
    ? Number(authorRow.pct) || 0
    : Math.max(0, round2(100 - tier.pct - totalPct(collaborators)));
  const bundleOn = typeof prices.bundle === 'number' && Number.isFinite(prices.bundle);

  return {
    prices: {
      paperback: text(prices.paperback, ''),
      ebook: text(prices.ebook, ''),
      bundle: bundleOn ? String(prices.bundle) : base.prices.bundle,
    },
    bundleOn,
    sold: {
      paperback: text(estimates.paperback, '0'),
      ebook: text(estimates.ebook, '0'),
      bundle: text(estimates.bundle, '0'),
    },
    platformTier: tier.id,
    parties: [
      { ...base.parties[0], pct: tier.pct },
      { ...base.parties[1], pct: authorPct },
      ...collaborators,
    ],
  };
}
